import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AppLayout } from '../components/layout';
import { Card, Button, Input, Select } from '../components/ui';
import useRolesStore from '../stores/rolesStore';

interface RoleAssignment {
	id: number;
	role: string;
	percentage: number;
}

interface Product {
	id: string;
	name: string;
	quantity: number;
	price: number;
}

const TCR = () => {
	const navigate = useNavigate();
	const { roles } = useRolesStore();
	const [hairType, setHairType] = useState<'synthetic' | 'curly'>('curly');
	const [hairQuantity, setHairQuantity] = useState(2);
	const [roleAssignments, setRoleAssignments] = useState<RoleAssignment[]>([]);
	const [products, setProducts] = useState<Product[]>([]);
	const [customerTip, setCustomerTip] = useState(0);
	const [workNotes, setWorkNotes] = useState('');
	const [customerFeedback, setCustomerFeedback] = useState('');
	const [roleIdCounter, setRoleIdCounter] = useState(0);

	const servicePrice = 280.0;
	const hairPrices = { synthetic: 8.0, curly: 14.5 };

	const productOptions = [
		{ id: 'edge-control', name: 'Edge Control', price: 4.2 },
		{ id: 'mousse', name: 'Mousse', price: 6.5 },
		{ id: 'braid-spray', name: 'Braid Spray', price: 5.0 },
		{ id: 'sheen-spray', name: 'Sheen Spray', price: 7.0 },
		{ id: 'gel', name: 'Styling Gel', price: 5.5 },
	];

	// Calculate costs
	const hairCost = hairQuantity * hairPrices[hairType];
	const productsCost = products.reduce((sum, p) => sum + p.price * p.quantity, 0);
	const totalMaterialsCost = hairCost + productsCost;
	const netAmount = servicePrice - totalMaterialsCost;
	const commissionPool = netAmount * 0.4;
	const totalPercentage = roleAssignments.reduce((sum, r) => sum + r.percentage, 0);

	const addRole = () => {
		setRoleAssignments([
			...roleAssignments,
			{
				id: roleIdCounter,
				role: roles[0]?.name || 'Liner',
				percentage: 100,
			},
		]);
		setRoleIdCounter(roleIdCounter + 1);
	};

	const removeRole = (id: number) => {
		setRoleAssignments(roleAssignments.filter(r => r.id !== id));
	};

	const updateRole = (id: number, field: 'role' | 'percentage', value: string | number) => {
		setRoleAssignments(
			roleAssignments.map(r => (r.id === id ? { ...r, [field]: field === 'percentage' ? Number(value) : value } : r)),
		);
	};

	const addProduct = (productId: string) => {
		const product = productOptions.find(p => p.id === productId);
		if (!product) return;

		const existingProduct = products.find(p => p.id === productId);
		if (existingProduct) {
			setProducts(products.map(p => (p.id === productId ? { ...p, quantity: p.quantity + 1 } : p)));
		} else {
			setProducts([...products, { ...product, quantity: 1 }]);
		}
	};

	const updateProductQuantity = (id: string, delta: number) => {
		setProducts(
			products
				.map(p => (p.id === id ? { ...p, quantity: Math.max(0, p.quantity + delta) } : p))
				.filter(p => p.quantity > 0),
		);
	};

	const totalEarnings =
		(totalPercentage > 0 ? commissionPool : 0) + customerTip;

	const handleSubmit = () => {
		if (roleAssignments.length === 0) {
			toast.error('Please add at least one role');
			return;
		}

		const submitPromise = new Promise(resolve => {
			setTimeout(() => {
				console.log('Submitting TCR:', {
					hairType,
					hairQuantity,
					roles: roleAssignments,
					products,
					customerTip,
					workNotes,
					customerFeedback,
					totalEarnings,
				});
				resolve(true);
			}, 1000);
		});

		toast.promise(submitPromise, {
			loading: 'Submitting report...',
			success: `Report submitted! Your estimated commission: $${totalEarnings.toFixed(2)}`,
			error: 'Failed to submit report',
		});

		submitPromise.then(() => {
			navigate('/dashboard');
		});
	};

	return (
		<AppLayout showTopbar={false}>
			<div
				className="flex items-center gap-2 text-blue-600 cursor-pointer hover:underline mb-4 text-sm font-medium"
				onClick={() => navigate('/bookings')}
			>
				<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
					<path
						fillRule="evenodd"
						d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
					/>
				</svg>
				Back to Bookings
			</div>

			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-900">Task Completion Report</h1>
				<p className="text-sm text-gray-600">Service: Custom Color + Blowout • Sarah Johnson</p>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				{/* Left Column - Form Fields */}
				<div className="lg:col-span-2 space-y-6">
					{/* Service Details */}
					<Card>
						<h3 className="text-lg font-bold mb-4">📋 Service Details</h3>
						<div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm space-y-1">
							<p>
								<strong>Service:</strong> Custom Color + Blowout
							</p>
							<p>
								<strong>Customer:</strong> Sarah Johnson
							</p>
							<p>
								<strong>Date:</strong> Jan 22, 2025 • 9:00 AM
							</p>
							<p>
								<strong>Price:</strong> ${servicePrice.toFixed(2)}
							</p>
						</div>
					</Card>

					{/* Hair Materials */}
					<Card>
						<h3 className="text-lg font-bold mb-4">💇 Hair Materials Used</h3>
						<div className="mb-4">
							<label className="block text-sm font-semibold text-gray-700 mb-2">Hair Type</label>
							<div className="grid grid-cols-2 gap-3">
								<button
									onClick={() => setHairType('synthetic')}
									className={`p-4 rounded-lg border-2 transition-all ${
										hairType === 'synthetic'
											? 'border-blue-600 bg-blue-50 text-blue-700'
											: 'border-gray-300 hover:border-blue-400'
									}`}
								>
									<div className="font-semibold">Synthetic</div>
									<div className="text-sm text-gray-600">$8/pack</div>
								</button>
								<button
									onClick={() => setHairType('curly')}
									className={`p-4 rounded-lg border-2 transition-all ${
										hairType === 'curly'
											? 'border-blue-600 bg-blue-50 text-blue-700'
											: 'border-gray-300 hover:border-blue-400'
									}`}
								>
									<div className="font-semibold">Curly</div>
									<div className="text-sm text-gray-600">$14.50/pack</div>
								</button>
							</div>
						</div>
						<div className="mb-4">
							<label className="block text-sm font-semibold text-gray-700 mb-2">Quantity (packs)</label>
							<div className="flex items-center gap-3">
								<button
									onClick={() => setHairQuantity(Math.max(0.5, hairQuantity - 0.5))}
									className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg font-bold"
								>
									−
								</button>
								<input
									type="number"
									value={hairQuantity}
									onChange={e => setHairQuantity(Math.max(0.5, parseFloat(e.target.value) || 0))}
									step="0.5"
									min="0.5"
									className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-center"
								/>
								<button
									onClick={() => setHairQuantity(hairQuantity + 0.5)}
									className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg font-bold"
								>
									+
								</button>
								<span className="text-gray-600">packs</span>
							</div>
						</div>
						<div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
							💡 {hairQuantity} packs = ${hairCost.toFixed(2)}
						</div>
					</Card>

					{/* Roles & Completion */}
					<Card>
						<div className="flex items-center justify-between mb-4">
							<h3 className="text-lg font-bold">👥 Your Roles & Completion</h3>
							<Button variant="primary" onClick={addRole} className="text-sm">
								+ Add Role
							</Button>
						</div>

						{roleAssignments.length === 0 ? (
							<div className="text-center py-8 text-gray-500">No roles added yet. Click "Add Role" to get started.</div>
						) : (
							<div className="space-y-3">
								{roleAssignments.map(roleAssignment => {
									const roleCommission =
										totalPercentage > 0 ? (commissionPool * roleAssignment.percentage) / totalPercentage : 0;

									return (
										<div key={roleAssignment.id} className="grid grid-cols-3 gap-3 items-center border border-gray-200 rounded-lg p-3">
											<div>
												<label className="text-xs text-gray-600 block mb-1">Role</label>
												<Select
													value={roleAssignment.role}
													onChange={e => updateRole(roleAssignment.id, 'role', e.target.value)}
													className="text-sm"
												>
													{roles.map(role => (
														<option key={role.id} value={role.name}>
															{role.name}
														</option>
													))}
												</Select>
											</div>
											<div>
												<label className="text-xs text-gray-600 block mb-1">Completion %</label>
												<Input
													type="number"
													value={roleAssignment.percentage}
													onChange={e => updateRole(roleAssignment.id, 'percentage', e.target.value)}
													min="0"
													max="100"
													className="text-sm"
												/>
											</div>
											<div className="flex flex-col items-center gap-2">
												<div className="bg-green-50 border border-green-200 rounded px-3 py-1 text-center">
													<div className="text-xs text-green-700">Commission</div>
													<div className="font-bold text-green-700">${roleCommission.toFixed(2)}</div>
												</div>
												<button
													onClick={() => removeRole(roleAssignment.id)}
													className="text-red-600 hover:bg-red-50 w-8 h-8 rounded-full font-bold text-xl"
												>
													×
												</button>
											</div>
										</div>
									);
								})}
							</div>
						)}

						<div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 mt-4">
							ℹ️ You can add multiple roles if you performed different tasks on this service
						</div>
					</Card>

					{/* Products Used */}
					<Card>
						<h3 className="text-lg font-bold mb-4">Products Used</h3>
						<Select
							value=""
							onChange={e => {
								if (e.target.value) {
									addProduct(e.target.value);
									e.target.value = '';
								}
							}}
							className="mb-4"
						>
							<option value="">Select product...</option>
							{productOptions.map(product => (
								<option key={product.id} value={product.id}>
									{product.name} (${product.price.toFixed(2)})
								</option>
							))}
						</Select>

						{products.length > 0 && (
							<div className="space-y-2">
								{products.map(product => (
									<div key={product.id} className="flex items-center justify-between border border-gray-200 rounded-lg p-3">
										<span className="font-medium">{product.name}</span>
										<div className="flex items-center gap-3">
											<div className="flex items-center gap-2">
												<button
													onClick={() => updateProductQuantity(product.id, -1)}
													className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
												>
													−
												</button>
												<span className="w-8 text-center">{product.quantity}</span>
												<button
													onClick={() => updateProductQuantity(product.id, 1)}
													className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
												>
													+
												</button>
											</div>
											<span className="font-semibold w-20 text-right">
												${(product.price * product.quantity).toFixed(2)}
											</span>
										</div>
									</div>
								))}
								<div className="flex justify-between font-bold pt-3 border-t border-gray-300">
									<span>Total Products:</span>
									<span>${productsCost.toFixed(2)}</span>
								</div>
							</div>
						)}
					</Card>

					{/* Customer Tip */}
					<Card>
						<div className="mb-4">
							<label className="block text-sm font-semibold text-gray-700 mb-2">Tip from Customer (Optional)</label>
							<div className="relative">
								<span className="absolute left-3 top-3 text-gray-600 font-semibold">$</span>
								<input
									type="number"
									value={customerTip}
									onChange={e => setCustomerTip(Math.max(0, parseFloat(e.target.value) || 0))}
									min="0"
									step="0.01"
									placeholder="0.00"
									className="w-full pl-8 px-3 py-2.5 border border-gray-300 rounded-lg"
								/>
							</div>
						</div>
					</Card>

					{/* Work Notes */}
					<Card>
						<label className="block text-sm font-semibold text-gray-700 mb-2">Work Notes (Optional)</label>
						<textarea
							value={workNotes}
							onChange={e => setWorkNotes(e.target.value.slice(0, 500))}
							placeholder="Add any notes about the work performed..."
							rows={3}
							className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm"
						/>
						<div className="text-xs text-gray-500 text-right mt-1">
							{workNotes.length}/500
						</div>
					</Card>

					{/* Customer Feedback */}
					<Card>
						<label className="block text-sm font-semibold text-gray-700 mb-2">Customer Feedback (Optional)</label>
						<textarea
							value={customerFeedback}
							onChange={e => setCustomerFeedback(e.target.value.slice(0, 500))}
							placeholder="Any feedback from the customer..."
							rows={3}
							className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm"
						/>
						<div className="text-xs text-gray-500 text-right mt-1">
							{customerFeedback.length}/500
						</div>
					</Card>
				</div>

				{/* Right Column - Summary */}
				<div className="lg:col-span-1">
					<div className="sticky top-6">
						<Card>
							<h3 className="text-lg font-bold mb-5">📊 Cost Summary</h3>

							<div className="space-y-3 text-sm mb-4">
								<div className="flex justify-between">
									<span className="text-gray-600">Service Price</span>
									<span className="font-semibold">${servicePrice.toFixed(2)}</span>
								</div>

								<div className="border-t border-gray-200 pt-3">
									<div className="text-gray-600 font-semibold mb-2">Materials Cost</div>
									<div className="ml-4 space-y-1">
										<div className="flex justify-between text-gray-700">
											<span>
												Hair ({hairType === 'curly' ? 'Curly' : 'Synthetic'})
											</span>
											<span>${hairCost.toFixed(2)}</span>
										</div>
										<div className="flex justify-between text-gray-700">
											<span>Products</span>
											<span>${productsCost.toFixed(2)}</span>
										</div>
										<div className="flex justify-between font-semibold pt-1 border-t border-gray-200">
											<span>Total Materials</span>
											<span>${totalMaterialsCost.toFixed(2)}</span>
										</div>
									</div>
								</div>

								<div className="border-t border-gray-200 pt-3">
									<div className="flex justify-between font-semibold">
										<span>Net Amount</span>
										<span>${netAmount.toFixed(2)}</span>
									</div>
									<div className="flex justify-between text-gray-600 mt-1">
										<span>Commission Pool (40%)</span>
										<span>${commissionPool.toFixed(2)}</span>
									</div>
								</div>

								<div className="border-t border-gray-200 pt-3">
									<div className="text-gray-600 font-semibold mb-2">Your Breakdown</div>
									<div className="ml-4 space-y-1">
										{roleAssignments.map(roleAssignment => {
											const roleCommission =
												totalPercentage > 0 ? (commissionPool * roleAssignment.percentage) / totalPercentage : 0;
											return (
												<div key={roleAssignment.id} className="flex justify-between text-sm">
													<span>
														{roleAssignment.role} ({roleAssignment.percentage}%)
													</span>
													<span>${roleCommission.toFixed(2)}</span>
												</div>
											);
										})}
										<div className="flex justify-between text-green-700 font-semibold pt-1 border-t border-gray-200">
											<span>Customer Tip</span>
											<span>${customerTip.toFixed(2)}</span>
										</div>
									</div>
								</div>
							</div>

							<div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 mb-5">
								<div className="text-sm text-green-700 font-semibold mb-1">Your Total Earnings</div>
								<div className="text-xs text-green-600 mb-3">
									Commission: ${(totalPercentage > 0 ? commissionPool : 0).toFixed(2)} + Tip: $
									{customerTip.toFixed(2)}
								</div>
								<div className="text-3xl font-bold text-green-700">${totalEarnings.toFixed(2)}</div>
							</div>

							<Button variant="primary" onClick={handleSubmit} className="w-full mb-3">
								Submit for Approval
							</Button>
							<Button variant="secondary" onClick={() => navigate('/bookings')} className="w-full">
								Cancel
							</Button>
						</Card>
					</div>
				</div>
			</div>

			<div className="mt-6 text-sm text-gray-500 text-center">© Beauty Business Portal</div>
		</AppLayout>
	);
};

export default TCR;
